import React, { useState } from 'react';
import { Box, Grid, Typography, Button, IconButton, Badge, Chip } from '@mui/material';
import { Link } from 'react-router-dom';
import { 
  Settings as SettingsIcon, 
  Person as PersonIcon,
  Add as AddIcon,
  Computer as ComputerIcon,
  Code as CodeIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Cloud as CloudIcon,
  Psychology as PsychologyIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon
} from '@mui/icons-material';
import ServerHealthChart from '../dashboard/ServerHealthChart';
import Notifications from '../dashboard/Notifications';
import TrainingJobs from '../dashboard/TrainingJobs';
import SystemActivity from '../dashboard/SystemActivity';
import DeployedAgents from '../dashboard/DeployedAgents';
import ServerGridDisplay from '../dashboard/ServerGridDisplay';

export default function Dashboard() {
  const [selectedMetric, setSelectedMetric] = useState('cpuUsage');
  const [selectedTimeframe, setSelectedTimeframe] = useState('hour');

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      p: { xs: 1, sm: 2, md: 2.5 },
      overflow: 'auto',
      backgroundColor: 'transparent',
      color: 'white',
      fontFamily: '"Inter", "IBM Plex Sans", sans-serif',
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: { xs: 2, sm: 2.5 },
        py: { xs: 1, sm: 1.5 },
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        gap: { xs: 2, sm: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" sx={{ 
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
          }}>
            Dashboard
          </Typography>
          <Chip 
            label="Coming Soon" 
            size="small" 
            color="warning" 
            sx={{ 
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              height: { xs: '20px', sm: '24px' }
            }}
          />
        </Box>

        <Box sx={{ 
          flexGrow: 0, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' }, 
          gap: { xs: 1, sm: 1 }
        }}>
          <Box sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 1 }
          }}>
            <Button
              component={Link}
              to="/training"
              startIcon={<AddCircleOutlineIcon />}
              size="small"
              fullWidth={window.innerWidth < 600}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                py: { xs: 1, sm: 0.5 },
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                minWidth: { xs: 'auto', sm: 'auto' }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                New Training Job
              </Box>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                New Training
              </Box>
            </Button>
            <Button
              component={Link}
              to="/cloud-deployment"
              startIcon={<CloudIcon />}
              size="small"
              fullWidth={window.innerWidth < 600}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                py: { xs: 1, sm: 0.5 },
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                minWidth: { xs: 'auto', sm: 'auto' }
              }}
            >
              Add Server
            </Button>
            <Button
              component={Link}
              to="/deployment"
              startIcon={<PsychologyIcon />}
              size="small"
              fullWidth={window.innerWidth < 600}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                py: { xs: 1, sm: 0.5 },
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                minWidth: { xs: 'auto', sm: 'auto' }
              }}
            >
              Agent Studio
            </Button>
          </Box>
          <Box sx={{ 
            display: 'flex',
            justifyContent: { xs: 'center', sm: 'flex-end' },
            gap: 1,
            mt: { xs: 1, sm: 0 }
          }}>
            <IconButton 
              color="inherit"
              size={window.innerWidth < 600 ? "small" : "medium"}
            >
              <Badge badgeContent={4} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <IconButton 
              color="inherit"
              size={window.innerWidth < 600 ? "small" : "medium"}
            >
              <AccountCircleIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
        <Grid item xs={12} lg={4}>
          <Box sx={{ 
            height: { xs: '250px', sm: '300px', lg: 'calc(50vh - 110px)' }, 
            mb: { xs: 1.5, sm: 2, lg: 2.5 }
          }}>
            <ServerHealthChart 
              selectedTimeframe={selectedTimeframe}
              setSelectedTimeframe={setSelectedTimeframe}
            />
          </Box>
          
          <Box sx={{ 
            height: { xs: '250px', sm: '300px', lg: 'calc(50vh - 110px)' }
          }}>
            <ServerGridDisplay 
              selectedMetric={selectedMetric}
              setSelectedMetric={setSelectedMetric}
            />
          </Box>
        </Grid>
        
        <Grid item xs={12} lg={4}>
          <Box sx={{ 
            height: { xs: '300px', sm: '350px', lg: 'calc(67vh - 120px)' }, 
            mb: { xs: 1.5, sm: 2, lg: 2.5 }
          }}>
            <Notifications />
          </Box>
          
          <Box sx={{ 
            height: { xs: '200px', sm: '250px', lg: 'calc(33vh - 100px)' }
          }}>
            <SystemActivity />
          </Box>
        </Grid>
        
        <Grid item xs={12} lg={4}>
          <Box sx={{ 
            height: { xs: '250px', sm: '300px', lg: 'calc(50vh - 110px)' }, 
            mb: { xs: 1.5, sm: 2, lg: 2.5 }
          }}>
            <TrainingJobs />
          </Box>
          
          <Box sx={{ 
            height: { xs: '250px', sm: '300px', lg: 'calc(50vh - 110px)' }
          }}>
            <DeployedAgents />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
